// Lecture automatique des clips vocaux reçus (répartiteur et chauffeur).
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { pttFlush } from "@/lib/pttQueue";

/**
 * Sonde /ptt/inbox, télécharge chaque clip et le joue à la suite sans interaction.
 * Renvoie l'état de lecture pour l'affichage (qui parle, combien en attente).
 */
export default function usePTTInbox({ enabled = true, pollMs = 5000, onIncoming } = {}) {
  const [playing, setPlaying] = useState(null);
  const [queueLen, setQueueLen] = useState(0);
  const [blocked, setBlocked] = useState(false);     // navigateur refuse la lecture auto
  const audioRef = useRef(null);
  const busy = useRef(false);
  const seen = useRef(new Set());

  const playClip = useCallback(async (clip) => {
    const { data } = await api.get(`/ptt/audio/${clip.id}`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const el = audioRef.current || new Audio();
    audioRef.current = el;
    el.src = url;
    el.volume = 1;
    setPlaying(clip);
    try {
      await el.play();
      setBlocked(false);
      await new Promise((res) => { el.onended = res; el.onerror = res; });
    } catch {
      setBlocked(true);                               // attend un geste de l'utilisateur
    } finally {
      URL.revokeObjectURL(url);
      setPlaying(null);
    }
    await api.post(`/ptt/${clip.id}/played`).catch(() => {});
  }, []);

  const drain = useCallback(async () => {
    if (!enabled || busy.current || document.hidden) return;
    busy.current = true;
    try {
      const { data } = await api.get("/ptt/inbox");
      setQueueLen(data.length);
      for (const clip of data) {
        if (seen.current.has(clip.id)) continue;
        seen.current.add(clip.id);
        if (onIncoming) onIncoming(clip);
        await playClip(clip);
      }
    } catch { /* hors réseau */ }
    finally { busy.current = false; }
  }, [enabled, onIncoming, playClip]);

  useEffect(() => {
    if (!enabled) return undefined;
    drain();
    const t = setInterval(drain, pollMs);
    const flush = () => pttFlush().catch(() => {});
    const tf = setInterval(flush, 30000);
    window.addEventListener("online", flush);
    return () => { clearInterval(t); clearInterval(tf); window.removeEventListener("online", flush); };
  }, [drain, enabled, pollMs]);

  /** À appeler sur un geste utilisateur pour débloquer la lecture automatique. */
  const unlock = useCallback(() => {
    const el = audioRef.current || new Audio();
    audioRef.current = el;
    el.muted = true;
    el.play().then(() => { el.pause(); el.muted = false; setBlocked(false); }).catch(() => {});
  }, []);

  return { playing, queueLen, blocked, unlock, replay: playClip };
}
